import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import AiBadge from "@via/components/ai-badge";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { TableSize } from "@via/components/table/table.tsx";
import { TruncatedText } from "@via/components/truncated-text.tsx";
import { useAbility } from "@via/context/ability-context";
import {
  useProject,
  useProjectRunList,
} from "@via/pages/projects/projects.api.tsx";
import DeleteRunModal from "@via/pages/projects/runs/delete-run-modal.tsx";
import RunPipelineLink from "@via/pages/projects/runs/run-pipeline-link.tsx";
import { getRunStatusLabel } from "@via/pages/projects/runs/utils/helpers.tsx";
import {
  EMPTY_TABLE_MESSAGE,
  ERROR,
  ROUTES,
  RUN_TABLE_OPTIONS,
} from "@via/utils/constants.tsx";
import { formatDateString } from "@via/utils/helpers.tsx";
import { type TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { TaggableEntityType, type ProjectRunResponse } from "common";
import { useCallback, useState, type JSX } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";
import TagsCell from "@via/components/tags-cell.tsx";
import TagValueInput from "@via/components/tag-value-input.tsx";

export default function Runs(): JSX.Element {
  const { projectId = "" } = useParams();
  const navigate = useNavigate();
  const { data: projectData } = useProject(projectId);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10); // Default page size
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>([
    {
      id: "createdAt",
      desc: true,
    },
  ]);
  const [isDeleteRunModalOpen, setIsDeleteRunModalOpen] = useState(false);
  const [runId, setRunId] = useState<string>("");
  const [runName, setRunName] = useState<string>("");
  const [tagFilter, setTagFilter] = useState<string>("");
  const { isLoading, isError, data } = useProjectRunList(
    projectId,
    page,
    pageSize,
    sort,
    tagFilter ? [tagFilter] : []
  );
  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: projectData?.own,
    },
  ]);

  const handleTableAction = (
    option: TableContextMenuInterface,
    name: string,
    id: number
  ): void => {
    switch (option.name) {
      case "View Run":
        navigate(`/runs/${id}`);
        break;
      case "Delete Run":
        setRunId(`${id}`);
        setRunName(name);
        setIsDeleteRunModalOpen(true);
        break;
      default:
        break;
    }
  };

  const getProjectRunsContext = useCallback(() => {
    const runs = data?.data || [];

    return {
      project: {
        id: projectData?.id,
        name: projectData?.name,
        own: projectData?.own,
      },
      runs: runs.map(run => ({
        id: run.id,
        name: run.name,
        status: run.status,
        owner: run.owner,
        pipelineName: run.pipelineName,
        pipelineId: run.pipelineId,
        pipelineVersion: run.pipelineVersion,
        type: run.type,
        createdAt: run.createdAt,
        own: run.own,
      })),
      total: data?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [data?.data, data?.total, projectData, page, pageSize, sort]);

  useRegisterCortexContext({
    routePattern: ROUTES.PROJECT_RUNS,
    contextName: "Project Runs",
    getContext: getProjectRunsContext,
    systemPrompt:
      "You are analyzing project runs list page. Help answer questions about runs in this project, help find specific runs, and assist with filtering and sorting.",
    enabled: !!data && !!projectData,
  });

  const columnHelper = createColumnHelper<ProjectRunResponse>();
  const columns = [
    columnHelper.accessor("id", {
      cell: info => info.getValue(),
      header: () => <span>ID</span>,
    }),
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return (
          <Link to={`/runs/${row.original.id}`} className="via-link truncate">
            <TruncatedText
              text={row.original.name}
              maxLength={40}
              id={`run-tooltip-${row.original.id}-${row.original.name}`}
            />
          </Link>
        );
      },
      header: () => <span>Run Name</span>,
    }),
    columnHelper.accessor("status", {
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            {getRunStatusLabel(row.original.status)}
            {Boolean(row.original.aiEntity) && <AiBadge />}
          </div>
        );
      },
      header: () => <span>Status</span>,
    }),
    columnHelper.accessor("owner", {
      cell: info => <span className="truncate">{info.getValue()}</span>,
      header: () => <span>Owner</span>,
    }),
    // We have to explicitly route and override the relative routing for pipelineName
    columnHelper.accessor("pipelineName", {
      cell: ({ row }) => {
        return (
          <RunPipelineLink
            pipelineId={row.original.pipelineId}
            pipelineName={row.original.pipelineName}
            subjectType={row.original.subjectType}
            sourceProcessId={row.original.sourceProcessId}
          />
        );
      },
      header: () => <span>Pipeline</span>,
    }),
    columnHelper.accessor("pipelineVersion", {
      cell: info => info.getValue() ?? "",
      header: () => <span>Version</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("type", {
      cell: info => (
        <span className="truncate">{info.getValue() ?? "Standard"}</span>
      ),
      header: () => <span>Type</span>,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return (
          <span className="truncate">{formatDateString(value, "LLL")}</span>
        );
      },
      header: () => <span>Date Created</span>,
    }),
    columnHelper.display({
      id: "tags",
      cell: ({ row }) => (
        <TagsCell tags={row.original.tags ?? []} rowId={row.original.id} />
      ),
      header: () => <span>Tags</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => {
        // Check if the user has permission to delete run
        const canDeleteRun = ability.can("View", "Button");

        // Filter the options based on the ability to delete
        const filteredOptions =
          canDeleteRun || row.original.own
            ? RUN_TABLE_OPTIONS
            : RUN_TABLE_OPTIONS.filter(option => option.name !== "Delete Run");

        return (
          <TableContextMenu
            options={filteredOptions}
            onSelect={option => {
              handleTableAction(option, row.original.name, row.original.id);
            }}
          />
        );
      },
      header: () => "Options",
      enableSorting: false,
    }),
  ] as Array<ColumnDef<ProjectRunResponse>>;

  return (
    <>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="w-64">
            <TagValueInput
              entityType={TaggableEntityType.Run}
              value={tagFilter}
              onChange={value => {
                setTagFilter(value);
                setPage(0);
              }}
            />
          </div>
          <Table
            data={data?.data}
            columns={columns}
            sort={sort}
            setSort={setSort}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            total={data?.total}
            emptyMessage={EMPTY_TABLE_MESSAGE.RUNS}
            size={TableSize.Big}
          />
        </div>
      )}
      {isDeleteRunModalOpen && (
        <DeleteRunModal
          open={isDeleteRunModalOpen}
          setOpen={setIsDeleteRunModalOpen}
          runName={runName}
          runId={runId}
        />
      )}
    </>
  );
}
